import SignupForm from '@components/SignupForm';
import { GraphQLClient } from 'graphql-request';
import OrganizationsGraphQL from '@graphql/organization.queries';
import { useLoader } from '@hooks/useLoader';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { publicSignupRequest } from '@models/organization.model';
import { Box, Card, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import logoText from '@assets/logos/mrwolf-black-completo-160@2x.png';
import WolfButton from '@components/ui/WolfButton';
import WolfTextLink from '@components/ui/WolfTextLink';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

const SignupPage: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [signupSuccess, setSignupSuccess] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isLoggedIn } = useAppSelector((state: RootState) => state.user);
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const { setLoading } = useLoader();

  // Redirect logged-in users away from signup page
  useEffect(() => {
    if (isLoggedIn && organizationId) {
      history.replace('/areas');
    }
  }, [isLoggedIn, organizationId, history]);
  const localeActionMessages = useTranslation('actionMessages');
  const localeCommon = useTranslation('common');
  const localeLogin = useTranslation('login');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Create a public GraphQL client without authentication
  const publicGraphQlClient = new GraphQLClient(
    process.env.REACT_APP_BACKEND_URL + 'graphql' || (process.env.REACT_APP_PUBLIC_GRAPHQL_API_URL as string),
  );

  const handleSignup = async (
    organizationName: string,
    organizationDescription: string,
    ownerEmail: string,
    ownerFirstName: string,
    ownerLastName: string,
  ) => {
    console.log('🚀 Starting signup process with data:', {
      organizationName,
      organizationDescription,
      ownerEmail,
      ownerFirstName,
      ownerLastName,
    });

    setLoading(true);
    setErrorMessage('');

    // Verify the mutation exists
    console.log('🔍 Available mutations:', Object.keys(OrganizationsGraphQL.mutations));
    console.log('🔍 publicSignup mutation:', OrganizationsGraphQL.mutations.publicSignup);

    try {
      console.log('📡 Sending GraphQL request...');
      const response: publicSignupRequest = await publicGraphQlClient.request(
        OrganizationsGraphQL.mutations.publicSignup,
        {
          organization: {
            name: organizationName,
            description: organizationDescription,
            ownerEmail: ownerEmail,
            ownerFirstName: ownerFirstName,
            ownerLastName: ownerLastName,
          },
        },
      );

      console.log('✅ GraphQL response received:', JSON.stringify(response, null, 2));

      if (response?.publicSignup?.organizationId) {
        console.log('✅ Signup successful, organization ID:', response.publicSignup.organizationId);
        setUserEmail(ownerEmail);
        setSignupSuccess(true);
        dispatch(
          appendActionMessage({
            message: localeActionMessages['accountCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      } else {
        console.log('❌ Signup response invalid:', response);
        setErrorMessage('Invalid response from server');
        // Note: Not dispatching notification since error is shown in form
      }
    } catch (error: any) {
      console.log('❌ GraphQL request failed:', error);
      console.log('❌ Error type:', typeof error);
      console.log('❌ Error message:', error?.message);
      console.log('❌ Error response:', error?.response);
      console.log('❌ Full error object:', JSON.stringify(error, null, 2));

      const errorMsg = error?.response?.errors?.[0]?.message || error?.message || 'Unknown error';
      console.log('❌ Processed error message:', errorMsg);

      if (errorMsg.includes('USER_ALREADY_EXISTS') || errorMsg.includes('already exists')) {
        setErrorMessage(localeActionMessages['userAlreadyExists']);
        dispatch(
          appendActionMessage({
            message: localeActionMessages['userAlreadyExists'],
            type: SnackType.ERROR,
          }),
        );
      } else {
        setErrorMessage(errorMsg || localeCommon['requestError']);
        dispatch(
          appendActionMessage({
            message: errorMsg || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const goToLanding = () => {
    window.location.href = process.env.REACT_APP_MARKETING_URL || '/';
  };

  const goToLogin = () => {
    history.push('/login');
  };

  // Email Verification Success Component
  const EmailVerificationSuccess = () => (
    <Box>
      <div onClick={goToLanding} style={{ cursor: 'pointer' }}>
        <img src={logoText} alt="logo" width="110" height="32" className="absolute top-5 left-5" />
        <WolfTextLink size="24px" align="right" className="absolute top-5 right-5" onClick={goToLanding}>
          {localeCommon['back']}
        </WolfTextLink>
      </div>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          maxWidth: isSmallScreen ? 300 : 400,
          minWidth: isSmallScreen ? 300 : 400,
          width: isSmallScreen ? 300 : 400,
        }}>
        <Typography variant="h1" sx={{ mb: 2, textAlign: 'center' }}>
          {localeLogin['emailVerificationSent']}
        </Typography>
        <Typography variant="body14" sx={{ mb: 4, textAlign: 'center' }}>
          {localeLogin['checkYourEmail']}
        </Typography>
        <Typography variant="body14" sx={{ mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
          {userEmail}
        </Typography>

        <WolfButton onClick={goToLogin} color="primary" fullWidth className="!my-4">
          {localeLogin['backToLogin']}
        </WolfButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
      }}>
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        {signupSuccess ? (
          <EmailVerificationSuccess />
        ) : (
          <SignupForm onSubmit={handleSignup} errorMessage={errorMessage} />
        )}
      </Card>
    </Box>
  );
};

export default SignupPage;
